import React from 'react';
import { formatDateTime } from 'helpers/dateTimeFormatter';
import { CHART_PLUGINS } from 'sharedComponents/Chart/constants';
import { Checkbox } from 'sharedComponents/Checkbox';
import { baseToCustomUOM, getMeasurementUnits, getMeasurementUnitSymbol } from 'helpers/unitHelper';
import { setMaxPrecision } from 'helpers/formatters';

export const TIMESTAMP_TABLE_COLUMN_KEYS = {
    RADIO: 'radio',
    TIMESTAMP: 'timestamp',
    SPEED: 'speed',
    LINE_FREQUENCY: 'lineFrequency',
    SKIN_TEMPERATURE: 'skinTemperature'
};

export const SORT_DIRECTION = {
    ASC: 'asc',
    DESC: 'desc'
};

export const TEMPERATURE_UNIT_GROUP_ID = 4;
export const MAX_PRECISION = 2;

export const TYPE_CONSTANTS = {
    TIMESTAMP: 'timestamp',
    NUMBER: 'number'
};

export const actionTypes = {
    GET_FFT_V2_ACTION: 'operationalParameters_v2/GET_FFT_V2_ACTION',
    GET_2D_FFT_V2_ACTION: 'operationalParameters_v2/GET_2D_FFT_V2_ACTION',
    GET_FFT_V2_REQUEST: 'operationalParameters_v2/GET_FFT_V2_REQUEST',
    GET_FFT_V2_TIMESTAMP_LIST_ACTION: 'operationalParameters_v2/GET_FFT_V2_TIMESTAMP_LIST_ACTION',
    GET_FFT_V2_TIMESTAMP_LIST_REQUEST: 'operationalParameters_v2/GET_FFT_V2_TIMESTAMP_LIST_REQUEST',
    GET_FFT_V2_DATA_BY_TIMESTAMP_REQUEST: 'operationalParameters_v2/GET_FFT_V2_DATA_BY_TIMESTAMP_REQUEST',
    SET_DATE_SELECTION: 'overview/SET_DATE_SELECTION',
    SET_FFT_V2_SELECTED_TIMESTAMPS: 'operationalParameters_v2/SET_FFT_V2_SELECTED_TIMESTAMPS',
    SET_2D_FFT_V2_SELECTED_TIMESTAMP: 'operationalParameters_v2/SET_2D_FFT_V2_SELECTED_TIMESTAMP',
    SET_FFT_V2_SELECTED_LEGENDS: 'operationalParameters_v2/SET_FFT_V2_SELECTED_LEGENDS',
    SET_2D_FFT_V2_SELECTED_LEGENDS: 'operationalParameters_v2/SET_2D_FFT_V2_SELECTED_LEGENDS',
    SET_FFT_V2_SELECTED_SPECTRUM_RANGE: 'operationalParameters_v2/SET_FFT_V2_SELECTED_SPECTRUM_RANGE',
    SET_FFT_V2_SELECTED_CHART_VIEW: 'operationalParameters_v2/SET_FFT_V2_SELECTED_CHART_VIEW',
    SET_FFT_V2_SELECTED_XAXIS_UNIT: 'operationalParameters_v2/SET_FFT_V2_SELECTED_XAXIS_UNIT',
    SET_FFT_V2_CHART_LOADER: 'operationalParameters_v2/SET_FFT_V2_CHART_LOADER',
    SET_FFT_BEARING_FAULT_SWITCH: 'operationalParameters_v2/SET_FFT_BEARING_FAULT_SWITCH',
    SET_FFT_BEARING_FAULT_TYPE: 'operationalParameters_v2/SET_FFT_BEARING_FAULT_TYPE',
    SET_FFT_BEARING_FAULT_FREQUENCIES: 'operationalParameters_v2/SET_FFT_BEARING_FAULT_FREQUENCIES',
    SET_FFT_SPEED_HARMONICS_SWITCH: 'operationalParameters_v2/SET_FFT_SPEED_HARMONICS_SWITCH',
    SET_FFT_V2_ECHART_VIEW: 'operationalParameters_v2/SET_FFT_V2_ECHART_VIEW',
    SET_FFT_V2_ECHART_LEGEND: 'operationalParameters_v2/SET_FFT_V2_ECHART_LEGEND',
    SET_FFT_V2_ECHART_BAND_SETTINGS: 'operationalParameters_v2/SET_FFT_V2_ECHART_BAND_SETTINGS',
    SET_FFT_V2_ECHART_BEARING_TYPES: 'operationalParameters_v2/SET_FFT_V2_ECHART_BEARING_TYPES',
    SET_FFT_V2_ECHART_FREQUENCY_RANGE: 'operationalParameters_v2/SET_FFT_V2_ECHART_FREQUENCY_RANGE',
};

export const chartModules = [CHART_PLUGINS.NO_DATA_TO_DISPLAY];

export const nameplateFrequency = 60; // Fixed constant value as line frequency varies dynamically

export const chartHeightConstants = {
    default: 700,
    min: 300,
    max: 1000,
    reserved: 160
};


export const frequencyMax = {
    'MSR': 12800,
    'CoMo': 820,
    'Gen1': 125,
    'Edwin': 1665
};

export const sensorTypesID = {
    'Motor': 1,
    'Pump': 2,
    'Bearing': 3,
    'CoMo': 5,
    'CoMoLight': 9,
    'CoMoOG': 10,
    'MSR': 6
};

export const chartViews = {
    FFT2D: '2D',
    FFT3D: '3D',
    ECHART3D: 'EChart3D'
};

export const PiezoSensor = {
    axisName: 'PiezoFFTJsonData',
    id: 'Piezo'
};

export const sensorTypes = {
    ACC: 'acc',
    PIEZZO: 'piezzo'
};

export const fftSelectionMax = (sensorType) => {
    switch (sensorType) {
        case 'MACHsense-R':
            return 2;
        default:
            return 6;
    }
};

export const getColumns = (selectedTimestamps, setSelectedTimestamp, trendKPIsList = [], maxPrecision = 2) => {
    // Helper function to get unit from trendKPIsList
    const getUnit = (timeseriesKey) => {
        if (!trendKPIsList || !Array.isArray(trendKPIsList)) {
            return '';
        }
        // Flatten the nested structure and find the matching timeseries
        const allKPIs = trendKPIsList?.flatMap(kpiGroup => {
            if (kpiGroup?.data) {
                return Object.values(kpiGroup?.data);
            }
            return [];
        });
        const kpi = allKPIs.find(k => k?.timeseriesKey === timeseriesKey);
        return kpi?.unit || '';
    };

    const speedUnit = getUnit('Speed');
    const lineFrequencyUnit = getUnit('LineFrequency');

    return [
        {
            width: 50,
            key: 'radio',
            title: '',
            render: (_, { timestamp }) =>
                <Checkbox
                    id={timestamp}
                    checked={selectedTimestamps.includes(timestamp)}
                    onToggle={() => setSelectedTimestamp(timestamp)}
                />
        },
        {
            title: '',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 250,
            render: (timestamp) => formatDateTime(timestamp)
        },
        {
            title: '',
            dataIndex: 'speed',
            key: 'speed',
            width: 200,
            render: (speed) => {
                if (speed !== null && speed !== undefined) {
                    const roundedSpeed = setMaxPrecision(speed, maxPrecision);
                    return `${roundedSpeed} ${speedUnit}`;
                }
                return '-';
            }
        },
        {
            title: '',
            dataIndex: 'lineFrequency',
            key: 'lineFrequency',
            width: 200,
            render: (lineFrequency) => {
                if (lineFrequency !== null && lineFrequency !== undefined) {
                    const roundedLineFrequency = setMaxPrecision(lineFrequency, maxPrecision);
                    return `${roundedLineFrequency} ${lineFrequencyUnit}`;
                }
                return '-';
            }
        },
        {
            title: '',
            dataIndex: 'skinTemperature',
            key: 'skinTemperature',
            width: 200,
            render: (skinTemperature) => {
                if (skinTemperature !== null && skinTemperature !== undefined) {
                    const convertedValue = baseToCustomUOM?.convertWithGroups(4, skinTemperature, getMeasurementUnits());
                    const roundedValue = setMaxPrecision(convertedValue, maxPrecision);
                    const unit = getMeasurementUnitSymbol(4) || '°C';
                    return `${roundedValue} ${unit}`;
                }
                return '-';
            }
        },
    ];
};

export const fftSignals = {
    Axial: 'Axial',
    Tangential: 'Tangential',
    Radial: 'Radial',
    Piezo: 'Piezo'
};

export const fftColors = {
    axialColors: ['#004480', '#005CA3', '#1B7AC2', '#00ACEC', '#2FC8F7', '#6CD5FF'],
    tangentialColors: ['#7A097A', '#8F188F', '#A13FA1', '#CC50CC', '#E670E6', '#F291F2'],
    radialColors: ['#751A08', '#A82005', '#D93616', '#ED5739', '#F57C64', '#FF9F8C']
};

export const fftColorsMSR = {
    NDE: {
        Axial: 'rgba(122, 9, 122, 1)',
        Horizontal: 'rgba(161, 63, 161, 1)',
        Vertical: 'rgba(230, 112, 230, 1)',
    },
    DE: {
        Axial: 'rgba(0, 68, 128, 1)',
        Horizontal: 'rgba(27, 122, 194, 1)',
        Vertical: 'rgba(47, 200, 247, 1)',
    },
};

export const default3DOptions = {
    1: {
        enabled: true,
        alpha: 12,
        beta: 0.09,
        depth: 250,
        viewDistance: 4,
        fitToPlot: true,
        areaDepth: 60,
    },
    2: {
        enabled: true,
        alpha: 12,
        beta: 0.09,
        depth: 350,
        viewDistance: 3,
        fitToPlot: true,
        areaDepth: 40,
    },
    3: {
        enabled: true,
        alpha: 12,
        beta: 0.09,
        depth: 500,
        viewDistance: 2,
        fitToPlot: true,
        areaDepth: 40,
    },
    4: {
        enabled: true,
        alpha: 12,
        beta: 0.09,
        depth: 400,
        viewDistance: 3,
        fitToPlot: true,
        areaDepth: 25,
    },
    5: {
        enabled: true,
        alpha: 12,
        beta: 0.09,
        depth: 400,
        viewDistance: 2,
        fitToPlot: true,
        areaDepth: 20,
    },
    6: {
        enabled: true,
        alpha: 12,
        beta: 0.09,
        depth: 500,
        viewDistance: 2,
        fitToPlot: true,
        areaDepth: 20,
    }
};
